import { Component, PureComponent } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { StatusDisplay, type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { type BillDocumentType } from "entity/bill/Bill/Bill.api.types";
import {
    type BillStatusType,
    getDocumentTypeStatusOverride,
} from "entity/bill/common/BillStatus/BillStatus";

export enum POPaymentStatus {
    Open = 0,
    ReadyForPayment = 1,
    Paid = 2,
    Void = 3,
    UnderSubReview = 4,
    OnlinePaymentProcessing = 5,
    OnlinePaymentDeclined = 6,
    OnlinePaymentFailed = 7,
    InReview = 8,
    Draft = 9,
    PaidHoldingRetainage = 10,
    NotApplicable = -1,
}

export enum POPaidStatus {
    Pending = 1,
    Voided = 2,
    FullyPaid = 3,
    PartiallyPaid = 4,
    NotPaid = 5,
    PaymentRequestedBySub = 6,
    ReadyForPayment = 7,
}

export const POPaymentStatusDescription = new Map<POPaymentStatus, string>([
    [POPaymentStatus.Open, "Open"],
    [POPaymentStatus.ReadyForPayment, "Ready For Payment"],
    [POPaymentStatus.Paid, "Paid"],
    [POPaymentStatus.PaidHoldingRetainage, "Paid Holding Retainage"],
    [POPaymentStatus.Void, "Void"],
    [POPaymentStatus.UnderSubReview, "Under Sub/Vendor Review"],
    [POPaymentStatus.OnlinePaymentProcessing, "Online Payment Processing"],
    [POPaymentStatus.OnlinePaymentDeclined, "Online Payment Declined"],
    [POPaymentStatus.OnlinePaymentFailed, "Online Payment Failed"],
    [POPaymentStatus.InReview, "In Review"],
    [POPaymentStatus.Draft, "Draft"],
    [POPaymentStatus.NotApplicable, "N/A"],
]);

export const getStatusText = (
    status: POPaymentStatus,
    isSubRequested?: boolean,
    documentType?: BillDocumentType,
): string => {
    // POPaymentStatus and BillStatusType share identical numeric codes for the
    // overridden statuses; the shared override lookup is keyed by BillStatusType.
    const overrideText = getDocumentTypeStatusOverride(
        documentType,
        status as unknown as BillStatusType,
    );
    if (overrideText !== undefined) {
        return overrideText;
    }
    let statusTagText = POPaymentStatusDescription.get(status)!;
    if (isSubRequested && POPaymentStatus.Open === status) {
        statusTagText = "Requested";
    }
    if (status === POPaymentStatus.NotApplicable) {
        statusTagText = POPaymentStatusDescription.get(POPaymentStatus.Open)!;
    }
    return statusTagText;
};

export const POPaymentStatusType = (status: POPaymentStatus): StatusTypes => {
    switch (status) {
        case POPaymentStatus.Paid:
        case POPaymentStatus.PaidHoldingRetainage:
            return "success";
        case POPaymentStatus.Void:
        case POPaymentStatus.OnlinePaymentDeclined:
        case POPaymentStatus.OnlinePaymentFailed:
            return "danger";
        case POPaymentStatus.UnderSubReview:
        case POPaymentStatus.OnlinePaymentProcessing:
        case POPaymentStatus.InReview:
            return "pending";
        default:
            return "";
    }
};

interface IPurchaseOrderPaymentStatusProps {
    status: POPaymentStatus;
    isSubRequested: boolean;
    details?: string | JSX.Element | null;
    actionButtons?: React.ReactNode;
    documentType?: BillDocumentType;
}

export class PurchaseOrderPaymentStatus extends Component<IPurchaseOrderPaymentStatusProps> {
    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof BuilderInfoContext>;

    render() {
        const { status, isSubRequested, documentType } = this.props;
        if (status < -1 || status > POPaymentStatusDescription.size - 1) {
            return "";
        } else {
            return (
                <StatusDisplay
                    statusType={POPaymentStatusType(status)}
                    details={this.props.details}
                    data-testid="statusTextContainer"
                    actionButtons={this.props.actionButtons}
                >
                    {getStatusText(status, isSubRequested, documentType)}
                </StatusDisplay>
            );
        }
    }
}

interface IPurchaseOrderPaymentStatusTagProps {
    status: POPaymentStatus;
    statusText?: string;
    isSubRequested?: boolean;
    documentType?: BillDocumentType;
}

export class PurchaseOrderPaymentStatusTag extends PureComponent<IPurchaseOrderPaymentStatusTagProps> {
    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof BuilderInfoContext>;

    render() {
        const { status, statusText, isSubRequested, documentType } = this.props;
        return (
            <StatusTagDisplay
                statusText={
                    isSubRequested
                        ? getStatusText(status, isSubRequested, documentType)
                        : statusText || getStatusText(status, isSubRequested, documentType)
                }
                statusType={POPaymentStatusType(status)}
            />
        );
    }
}
